import { useMemo } from "react";
import { Link as RouterLink } from "react-router";
import { ArrowUpRight, FlaskConical } from "lucide-react";
import { StatusBadge } from "@bastion/ui";
import type { ToolRendererProps } from "@bastion/assistant";
import {
  applyFilters,
  type FilterState,
} from "@bastion/list-view";
import {
  WORKFLOW_RUN_STATUS_COLORS,
  WORKFLOW_RUN_STATUS_LABELS,
  applyWorkflowTabToFilters,
  formatDuration,
  humanizeStageName,
  useWorkflowRuns,
  type WorkflowRun,
  type WorkflowTabId,
} from "@bastion/ml-studio";
import { buildWorkflowFiltersTargetPath } from "@/app/assistantTools";
import styles from "./InlineWorkflowResults.module.css";

const DEFAULT_LIMIT = 8;
const MAX_LIMIT = 25;

interface ParsedArgs {
  title: string | null;
  limit: number;
  filterState: FilterState;
  targetHref: string;
}

function toArray(value: unknown): string[] {
  if (value == null) return [];
  const arr = Array.isArray(value) ? value : [value];
  const out: string[] = [];
  for (const item of arr) {
    if (typeof item !== "string") continue;
    const trimmed = item.trim();
    if (!trimmed) continue;
    if (!out.includes(trimmed)) out.push(trimmed);
  }
  return out;
}

function isWorkflowTab(value: unknown): value is WorkflowTabId {
  return value === "active" || value === "completed" || value === "all";
}

function parseArgs(args: Record<string, unknown>): ParsedArgs {
  let filterState: FilterState = { filters: [] };
  const status = toArray(args.status);
  if (status.length > 0) {
    filterState.filters.push({ attributeId: "status", operator: "is", values: status });
  }
  const tag = toArray(args.tag);
  if (tag.length > 0) {
    filterState.filters.push({ attributeId: "tag", operator: "is", values: tag });
  }
  const workflowName = toArray(args.workflow_name);
  if (workflowName.length > 0) {
    filterState.filters.push({
      attributeId: "workflow_name",
      operator: "is",
      values: workflowName,
    });
  }
  // Tab shortcut folds into the status chip when no explicit status is set.
  const tabRaw = args.tab ?? args.preset;
  if (isWorkflowTab(tabRaw) && tabRaw !== "all" && status.length === 0) {
    filterState = applyWorkflowTabToFilters(filterState, tabRaw);
  }
  const limitRaw = typeof args.limit === "number" ? Math.floor(args.limit) : DEFAULT_LIMIT;
  const limit = Math.max(1, Math.min(MAX_LIMIT, limitRaw));
  const title = typeof args.title === "string" && args.title.trim() ? args.title.trim() : null;
  return {
    title,
    limit,
    filterState,
    targetHref: buildWorkflowFiltersTargetPath(args),
  };
}

function getWorkflowFilterValue(
  run: WorkflowRun,
  attributeId: string,
): string | string[] | null | undefined {
  if (attributeId === "status") return run.status;
  if (attributeId === "workflow_name") return run.workflow_name || run.name || "Ungrouped";
  if (attributeId === "tag") return run.tags ?? [];
  return null;
}

function getRunLabel(run: WorkflowRun): string {
  if (run.description) {
    const firstLine = run.description.split("\n").map((line) => line.trim()).find(Boolean);
    if (firstLine) {
      return firstLine
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1")
        .replace(/^\s{0,3}#{1,6}\s+/, "")
        .replace(/^\s*[-*+]\s+/, "")
        .replace(/^\s*\d+\.\s+/, "")
        .replace(/(\*\*|__|\*|_|`|~~)/g, "")
        .replace(/\s+/g, " ")
        .trim();
    }
  }
  return `Run ${run.id.slice(0, 8)}`;
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export interface InlineWorkflowResultsViewProps {
  title: string | null;
  rows: WorkflowRun[];
  totalCount: number;
  limit: number;
  targetHref: string;
  isLoading?: boolean;
  errorMessage?: string | null;
}

/**
 * Pure presentational view for inline workflow results. Used by the
 * `render_workflow_results` tool renderer and by the design page (with mock
 * data) so we don't have to wire `useWorkflowRuns` into the design surface.
 */
export function InlineWorkflowResultsView({
  title,
  rows,
  totalCount,
  limit,
  targetHref,
  isLoading = false,
  errorMessage = null,
}: InlineWorkflowResultsViewProps) {
  const visible = rows.slice(0, limit);
  const remainder = Math.max(0, totalCount - visible.length);
  const heading = title ?? "Workflow runs";

  return (
    <div className={styles.card} data-testid="inline-workflow-results">
      <header className={styles.header}>
        <FlaskConical className={styles.headerIcon} aria-hidden style={{ width: 14, height: 14 }} />
        <h3 className={styles.heading}>{heading}</h3>
        <span className={styles.count}>{totalCount} {totalCount === 1 ? "run" : "runs"}</span>
      </header>
      {errorMessage ? (
        <p className={styles.errorMessage}>Error: {errorMessage}</p>
      ) : isLoading ? (
        <p className={styles.emptyMessage}>Loading workflow runs…</p>
      ) : visible.length === 0 ? (
        <p className={styles.emptyMessage}>No workflow runs match these filters.</p>
      ) : (
        <ul className={styles.rows}>
          {visible.map((run) => (
            <li key={run.id} className={styles.row}>
              <div className={styles.rowMain}>
                <RouterLink
                  to={`/ml-studio/workflows/${run.id}`}
                  className={styles.runName}
                  title={getRunLabel(run)}
                >
                  {getRunLabel(run)}
                </RouterLink>
                <span className={styles.workflowName}>
                  {run.workflow_name != null
                    ? humanizeStageName(run.workflow_name)
                    : "Ungrouped"}
                </span>
              </div>
              <StatusBadge
                label={WORKFLOW_RUN_STATUS_LABELS[run.status] || run.status}
                className={styles.statusBadge}
                {...(WORKFLOW_RUN_STATUS_COLORS[run.status] || {})}
              />
              <span className={styles.time}>
                {relativeTime(run.created_at)}
                {(run.status === "running" || run.status === "waiting_for_gpu") && (
                  <span className={styles.duration}> · {formatDuration(run.created_at)}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      <footer className={styles.footer}>
        <RouterLink to={targetHref} className={styles.footerLink}>
          {remainder > 0
            ? `View ${remainder} more in Workflows`
            : "Open Workflows with these filters"}
          <ArrowUpRight style={{ width: 12, height: 12 }} aria-hidden />
        </RouterLink>
      </footer>
    </div>
  );
}

export function InlineWorkflowResults({ toolCall }: ToolRendererProps) {
  const parsed = useMemo(() => parseArgs(toolCall.args), [toolCall.args]);
  const { data: workflows, isLoading, error } = useWorkflowRuns();

  const filteredRuns = useMemo(() => {
    if (!workflows) return [];
    return applyFilters(workflows, parsed.filterState, getWorkflowFilterValue);
  }, [workflows, parsed.filterState]);

  return (
    <InlineWorkflowResultsView
      title={parsed.title}
      rows={filteredRuns}
      totalCount={filteredRuns.length}
      limit={parsed.limit}
      targetHref={parsed.targetHref}
      isLoading={isLoading}
      errorMessage={error ? (error as Error).message : null}
    />
  );
}
